export type HomeActivityDay = {
  date: string;
  from: string;
  to: string;
  conversations: number;
  reservationIntents: number;
  bookings: number;
};

const WEEKDAYS = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
const WEEKDAY_NAMES = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MONTH_NAMES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
const MESSAGES = '#38bdf8';
const INTENTS = '#7c3aed';
const BOOKINGS = '#22c55e';

const WIDTH = 440;
const HEIGHT = 188;
const PAD_L = 34;
const PAD_R = 8;
const PAD_T = 10;
const PAD_B = 36;

function weekdayIndex(dateKey: string): number {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year!, month! - 1, day!)).getUTCDay();
}

function dayNumber(dateKey: string): string {
  return String(Number(dateKey.slice(8, 10)));
}

function formatCount(count: number): string {
  return Math.trunc(count).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function axisLabel(value: number): string {
  if (value >= 1000) {
    const scaled = value / 1000;
    const text = Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(1).replace('.', ',');
    return `${text}k`;
  }
  return formatCount(value);
}

function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

function formatDay(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const weekday = new Date(Date.UTC(year!, month! - 1, day!)).getUTCDay();
  return `${WEEKDAY_NAMES[weekday]} ${day} ${MONTH_NAMES[month! - 1]}`;
}

export function HomeActivityChart({
  days,
  selectedDate,
  onSelectDate,
}: {
  days: HomeActivityDay[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}) {
  const selected = days.findIndex((day) => day.date === selectedDate);
  const active = selected >= 0 ? days[selected] : null;
  const max = Math.max(1, ...days.flatMap((day) => [day.conversations, day.reservationIntents, day.bookings]));
  const plotW = WIDTH - PAD_L - PAD_R;
  const plotH = HEIGHT - PAD_T - PAD_B;
  const slot = days.length > 0 ? plotW / days.length : plotW;
  const mid = max >= 4 ? Math.round(max / 2) : null;
  const ticks = mid != null && mid !== max && mid !== 0 ? [max, mid, 0] : [max, 0];

  return (
    <section className="home-activity pwa-card" aria-label="Actividad de las últimas 2 semanas">
      <h2 className="home-activity__title">Últimas 2 semanas</h2>
      <svg
        className="home-activity__svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="Conversaciones nuevas, intención de reserva y reservas por día"
      >
        {ticks.map((tick) => {
          const y = PAD_T + plotH - (tick / max) * plotH;
          return (
            <g key={tick}>
              <line x1={PAD_L} x2={WIDTH - PAD_R} y1={y} y2={y} className="home-activity__grid" />
              <text x={PAD_L - 6} y={y + 3} textAnchor="end" className="home-activity__axis">
                {axisLabel(tick)}
              </text>
            </g>
          );
        })}
        {days.map((day, index) => {
          const center = PAD_L + slot * index + slot / 2;
          const barH = (value: number) => (value > 0 ? Math.max(3, (value / max) * plotH) : 0);
          const messageH = barH(day.conversations);
          const intentH = barH(day.reservationIntents);
          const bookingH = barH(day.bookings);
          const base = PAD_T + plotH;
          const on = index === selected;
          return (
            <g
              key={day.date}
              className={on ? 'home-activity__col home-activity__col--on' : 'home-activity__col'}
              role="button"
              tabIndex={0}
              aria-pressed={on}
              aria-label={`${formatDay(day.date)}: ${plural(day.conversations, 'conversación nueva', 'conversaciones nuevas')}, ${plural(day.reservationIntents, 'intención de reserva', 'intenciones de reserva')}, ${plural(day.bookings, 'reserva', 'reservas')}`}
              onClick={() => onSelectDate(day.date)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectDate(day.date);
                }
              }}
            >
              <rect
                className="home-activity__hit"
                x={PAD_L + slot * index}
                y={PAD_T}
                width={slot}
                height={plotH}
              />
              {messageH > 0 ?
                <rect
                  x={center - slot * 0.36}
                  y={base - messageH}
                  width={slot * 0.72}
                  height={messageH}
                  rx={2}
                  fill={MESSAGES}
                />
              : null}
              {intentH > 0 ?
                <rect
                  x={center - slot * 0.24}
                  y={base - intentH}
                  width={slot * 0.48}
                  height={intentH}
                  rx={2}
                  fill={INTENTS}
                />
              : null}
              {bookingH > 0 ?
                <rect
                  x={center - slot * 0.14}
                  y={base - bookingH}
                  width={slot * 0.28}
                  height={bookingH}
                  rx={2}
                  fill={BOOKINGS}
                />
              : null}
              <text x={center} y={HEIGHT - 18} textAnchor="middle" className="home-activity__axis">
                {WEEKDAYS[weekdayIndex(day.date)]}
              </text>
              <text x={center} y={HEIGHT - 6} textAnchor="middle" className="home-activity__axis">
                {dayNumber(day.date)}
              </text>
            </g>
          );
        })}
      </svg>
      <ul className="home-activity__legend">
        <li>
          <span className="home-activity__swatch" style={{ background: MESSAGES }} />
          Conversaciones nuevas
        </li>
        <li>
          <span className="home-activity__swatch" style={{ background: INTENTS }} />
          Intención de reserva
        </li>
        <li>
          <span className="home-activity__swatch" style={{ background: BOOKINGS }} />
          Reservas
        </li>
      </ul>
      {active ?
        <p className="home-activity__detail">
          {formatDay(active.date)}: {plural(active.conversations, 'conversación nueva', 'conversaciones nuevas')},{' '}
          {plural(active.reservationIntents, 'intención', 'intenciones')},{' '}
          {plural(active.bookings, 'reserva', 'reservas')}
        </p>
      : <p className="home-activity__detail">Tocá una barra para ver ese día en el resto del inicio.</p>}
    </section>
  );
}
